import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from './lib/cn'

/* ==========================================================================
 * Spinner
 * ======================================================================== */

export interface SpinnerProps {
  className?: string
  /** Screen-reader label. Pass null when the spinner is purely decorative. */
  label?: string | null
}

export function Spinner({ className, label = 'Loading' }: SpinnerProps) {
  return (
    <span role={label ? 'status' : undefined} className="inline-flex">
      <Loader2 aria-hidden className={cn('h-4 w-4 animate-spin', className)} />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}

/* ==========================================================================
 * Button
 * ======================================================================== */

export const buttonVariants = cva(
  [
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
    'rounded-control font-medium transition-colors duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/60',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        /** The default action: Obsidian fill, Pearl text, inverted in dark. */
        primary: 'bg-primary text-primary-foreground shadow-card hover:bg-primary-hover',
        /** Steel fill. The signature accent, used sparingly. */
        accent: 'bg-accent text-accent-foreground shadow-card hover:bg-accent-hover',
        outline:
          'border border-line-strong bg-surface text-foreground hover:bg-sunken',
        ghost: 'text-muted hover:bg-sunken hover:text-foreground',
        /** Destructive. Uses the paired solid/on-solid tokens, which flip
            per theme so the label always clears contrast. */
        danger: 'bg-danger-solid text-danger-on-solid shadow-card hover:opacity-90',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-caption',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
      },
      /** Square, for a single icon. Always pair with aria-label. */
      iconOnly: {
        true: 'px-0',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'w-8' },
      { iconOnly: true, size: 'md', class: 'w-10' },
      { iconOnly: true, size: 'lg', class: 'w-12' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      iconOnly: false,
    },
  }
)

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  /** Swap the label for a spinner and block interaction. */
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export function Button({
  variant,
  size,
  iconOnly,
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading

  return (
    <button
      type={type}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      className={cn(buttonVariants({ variant, size, iconOnly }), className)}
      {...props}
    >
      {loading ? <Spinner className="shrink-0" /> : leftIcon}
      {children}
      {!loading && rightIcon ? rightIcon : null}
    </button>
  )
}

/* ==========================================================================
 * IconButton
 * ======================================================================== */

export interface IconButtonProps extends Omit<ButtonProps, 'iconOnly' | 'leftIcon' | 'rightIcon'> {
  /** Required — an icon-only control has no accessible name otherwise. */
  label: string
  icon: React.ReactNode
}

/** Square button holding a single icon. The label becomes its accessible name. */
export function IconButton({ label, icon, ...props }: IconButtonProps) {
  return (
    <Button aria-label={label} title={label} iconOnly {...props}>
      {icon}
    </Button>
  )
}

/* ==========================================================================
 * ButtonGroup
 * ======================================================================== */

export interface ButtonGroupProps extends React.ComponentProps<'div'> {
  /** Merge adjacent borders so the group reads as one control. */
  attached?: boolean
}

export function ButtonGroup({ attached = false, className, ...props }: ButtonGroupProps) {
  return (
    <div
      role="group"
      className={cn(
        'inline-flex items-center',
        attached
          ? '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:last-child)]:border-r-0'
          : 'gap-2',
        className
      )}
      {...props}
    />
  )
}

/* ==========================================================================
 * Kbd
 * ======================================================================== */

export function Kbd({ className, children, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-line',
        'bg-sunken px-1.5 font-mono text-[11px] font-medium text-muted',
        className
      )}
      {...props}
    >
      {children}
    </kbd>
  )
}
